// Recorded sounds for weapons and impacts. Players with CS:S get the game's own sounds (read from
// their game folder or weapon skin packs, never shipped with the site); everyone else gets Kenney's
// CC0 sounds from public/assets. Sounds missing from both are synthesised by Sound.

import { assetManifest, KENNEY_BASE } from '../assets/kenney';
import { CSS, type SampleId } from './cssSounds';

export type { SampleId };

/** Kenney fallbacks, by manifest sound group. */
const KENNEY: Partial<Record<SampleId, string>> = {
  knifeDeploy: 'knifeDraw',
  knifeSlash: 'swing',
  knifeHitWall: 'knifeHit',
  knifeHitMetal: 'knifeHitMetal',
  glassBreak: 'glassBreak',
  uspClipOut: 'metalClick',
  uspClipIn: 'metalLatch',
  uspSlideBack: 'metalLatch',
  uspSlideRelease: 'metalClick',
  impactConcrete: 'bulletHitConcrete',
  impactMetal: 'bulletHitMetal',
  impactWood: 'bulletHitWood',
  impactGlass: 'glass',
};

/** Decode a PCM WAV (8/16/24/32-bit int or 32-bit float). Null for anything else. */
export function decodeWav(ctx: BaseAudioContext, bytes: Uint8Array): AudioBuffer | null {
  if (bytes.length < 44) return null;
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const tag = (o: number) => String.fromCharCode(bytes[o], bytes[o + 1], bytes[o + 2], bytes[o + 3]);
  if (tag(0) !== 'RIFF' || tag(8) !== 'WAVE') return null;
  let fmt = -1;
  let channels = 0;
  let rate = 0;
  let bits = 0;
  let data = -1;
  let dataLen = 0;
  for (let o = 12; o + 8 <= bytes.length;) {
    const id = tag(o);
    const len = v.getUint32(o + 4, true);
    if (id === 'fmt ') {
      fmt = v.getUint16(o + 8, true);
      channels = v.getUint16(o + 10, true);
      rate = v.getUint32(o + 12, true);
      bits = v.getUint16(o + 22, true);
      // WAVE_FORMAT_EXTENSIBLE: the real format is the first two bytes of the sub-format GUID.
      if (fmt === 0xfffe && len >= 26) fmt = v.getUint16(o + 32, true);
    } else if (id === 'data') {
      data = o + 8;
      dataLen = Math.min(len, bytes.length - data);
    }
    o += 8 + len + (len & 1);
  }
  const float = fmt === 3 && bits === 32;
  if (data < 0 || channels < 1 || rate < 3000 || (fmt !== 1 && !float) || ![8, 16, 24, 32].includes(bits))
    return null;
  const step = bits / 8;
  const frames = Math.floor(dataLen / (step * channels));
  if (frames < 1) return null;
  const buf = ctx.createBuffer(channels, frames, rate);
  for (let c = 0; c < channels; c++) {
    const out = buf.getChannelData(c);
    for (let i = 0; i < frames; i++) {
      const o = data + (i * channels + c) * step;
      out[i] = float
        ? v.getFloat32(o, true)
        : bits === 8
          ? (bytes[o] - 128) / 128
          : bits === 16
            ? v.getInt16(o, true) / 32768
            : bits === 24
              ? ((bytes[o] | (bytes[o + 1] << 8) | (bytes[o + 2] << 16)) << 8) / 2147483648
              : v.getInt32(o, true) / 2147483648;
    }
  }
  return buf;
}

async function decodeAny(ctx: BaseAudioContext, bytes: Uint8Array): Promise<AudioBuffer | null> {
  const pcm = decodeWav(ctx, bytes);
  if (pcm) return pcm;
  try {
    // Compressed WAVs (MP3/ADPCM in skin packs) and Kenney's OGGs: let the browser try.
    return await ctx.decodeAudioData(bytes.slice().buffer);
  } catch {
    return null;
  }
}

export class SampleBank {
  private readonly game = new Map<SampleId, AudioBuffer[]>();
  private readonly kenney = new Map<SampleId, AudioBuffer[]>();
  private kenneyLoading = false;
  private last = new Map<SampleId, number>();

  /** Game sound files (path -> bytes) from the model loader; replaces the previous set. */
  async setGameFiles(ctx: BaseAudioContext, files: Map<string, Uint8Array>) {
    const next = new Map<SampleId, AudioBuffer[]>();
    for (const id of Object.keys(CSS) as SampleId[]) {
      const list: AudioBuffer[] = [];
      for (const p of CSS[id]) {
        const bytes = files.get(`sound/${p}.wav`);
        const b = bytes && (await decodeAny(ctx, bytes));
        if (b) list.push(b);
      }
      if (list.length) next.set(id, list);
    }
    this.game.clear();
    for (const [k, v] of next) this.game.set(k, v);
  }

  get hasGameSounds() {
    return this.game.size > 0;
  }

  /** Fetch the Kenney sounds once (after the first click, when audio is allowed). */
  loadKenney(ctx: BaseAudioContext) {
    if (this.kenneyLoading) return;
    this.kenneyLoading = true;
    void assetManifest().then(async (m) => {
      for (const [id, group] of Object.entries(KENNEY) as [SampleId, string][]) {
        const files = m.sounds[group] ?? [];
        const list: AudioBuffer[] = [];
        for (const f of files) {
          try {
            const r = await fetch(KENNEY_BASE + f);
            if (!r.ok) continue;
            const b = await decodeAny(ctx, new Uint8Array(await r.arrayBuffer()));
            if (b) list.push(b);
          } catch {
            // Offline or unsupported: the synthesised sound plays instead.
          }
        }
        if (list.length) this.kenney.set(id, list);
      }
    });
  }

  /** A recording for this sound (game first, then Kenney), not the same variant twice in a row. */
  pick(id: SampleId): { buffer: AudioBuffer; game: boolean } | null {
    const g = this.game.get(id);
    const list = g ?? this.kenney.get(id);
    if (!list) return null;
    let i = Math.floor(Math.random() * list.length);
    if (list.length > 1 && i === this.last.get(id)) i = (i + 1) % list.length;
    this.last.set(id, i);
    return { buffer: list[i], game: !!g };
  }
}
